import React, { useState } from 'react';
import { PlayIcon, PauseIcon, SkipBackIcon, SkipForwardIcon, ChevronDownIcon, VolumeIcon, TagIcon, MapPinIcon } from 'lucide-react';
import { TaggingInterface } from './TaggingInterface';
interface Track {
  id: string;
  title: string;
  artist: string;
  album: string;
  cover: string;
  tags: string[];
  location: {
    lat: number;
    lng: number;
  };
}
interface MusicPlayerProps {
  track: Track;
  isExpanded: boolean;
  onCollapse: () => void;
  onExpand: () => void;
}
export function MusicPlayer({
  track,
  isExpanded,
  onCollapse,
  onExpand
}: MusicPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTagging, setShowTagging] = useState(false);
  if (isExpanded) {
    return <div className="fixed inset-0 bg-gradient-to-b from-zinc-900 to-black z-10 overflow-y-auto" data-id="element-102">
        <div className="max-w-md mx-auto px-4 py-6 h-full flex flex-col" data-id="element-103">
          {/* Header */}
          <div className="flex items-center mb-8" data-id="element-104">
            <button onClick={onCollapse} className="text-zinc-400" data-id="element-105">
              <ChevronDownIcon size={24} data-id="element-106" />
            </button>
            <div className="flex-1 text-center" data-id="element-107">
              <p className="text-sm font-medium" data-id="element-108">Now Playing</p>
            </div>
            <div className="w-6" data-id="element-109"></div> {/* Spacer for alignment */}
          </div>
          {/* Album cover */}
          <div className="aspect-square w-full mb-8 relative" data-id="element-110">
            <img src={track.cover} alt={`${track.album} cover`} className="w-full h-full object-cover rounded-lg shadow-xl" data-id="element-111" />
            {/* Location indicator */}
            <div className="absolute bottom-4 right-4 bg-black/70 px-3 py-1.5 rounded-full flex items-center" data-id="element-112">
              <MapPinIcon size={16} className="text-green-500 mr-1.5" data-id="element-113" />
              <span className="text-xs" data-id="element-114">New York, USA</span>
            </div>
          </div>
          {/* Track info */}
          <div className="mb-8" data-id="element-115">
            <h2 className="text-2xl font-bold mb-1" data-id="element-116">{track.title}</h2>
            <p className="text-zinc-400" data-id="element-117">
              {track.artist} • {track.album}
            </p>
          </div>
          {/* Tags */}
          <div className="mb-6" data-id="element-118">
            <div className="flex items-center justify-between mb-2" data-id="element-119">
              <span className="text-sm text-zinc-400" data-id="element-120">Tags</span>
              <button onClick={() => setShowTagging(true)} className="flex items-center text-green-500 text-sm" data-id="element-121">
                <TagIcon size={14} className="mr-1" data-id="element-122" />
                <span data-id="element-123">Edit Tags</span>
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-124">
              {track.tags.map(tag => <span key={tag} className="px-2.5 py-1 bg-zinc-800 rounded-full text-xs" data-id="element-125">
                  {tag}
                </span>)}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mb-6" data-id="element-126">
            <div className="h-1 w-full bg-zinc-800 rounded-full overflow-hidden" data-id="element-127">
              <div className="h-full w-1/3 bg-green-500 rounded-full" data-id="element-128"></div>
            </div>
            <div className="flex justify-between mt-1" data-id="element-129">
              <span className="text-xs text-zinc-500" data-id="element-130">1:23</span>
              <span className="text-xs text-zinc-500" data-id="element-131">3:45</span>
            </div>
          </div>
          {/* Controls */}
          <div className="flex items-center justify-between mb-8" data-id="element-132">
            <button className="text-zinc-400" data-id="element-133">
              <SkipBackIcon size={24} data-id="element-134" />
            </button>
            <button className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center" onClick={() => setIsPlaying(!isPlaying)} data-id="element-135">
              {isPlaying ? <PauseIcon size={30} className="text-black" data-id="element-136" /> : <PlayIcon size={30} className="text-black ml-1" data-id="element-137" />}
            </button>
            <button className="text-zinc-400" data-id="element-138">
              <SkipForwardIcon size={24} data-id="element-139" />
            </button>
          </div>
          {/* Volume */}
          <div className="flex items-center" data-id="element-140">
            <VolumeIcon size={20} className="text-zinc-400 mr-3" data-id="element-141" />
            <div className="h-1 flex-1 bg-zinc-800 rounded-full overflow-hidden" data-id="element-142">
              <div className="h-full w-2/3 bg-zinc-400 rounded-full" data-id="element-143"></div>
            </div>
          </div>
          {/* Tagging interface */}
          {showTagging && <TaggingInterface currentTags={track.tags} onClose={() => setShowTagging(false)} data-id="element-144" />}
        </div>
      </div>;
  }
  // Mini player
  return <div className="fixed bottom-16 left-0 right-0 h-16 bg-zinc-900/95 backdrop-blur-md border-t border-zinc-800 flex items-center px-3" onClick={onExpand} data-id="element-145">
      <img src={track.cover} alt={`${track.album} cover`} className="h-12 w-12 rounded object-cover mr-3" data-id="element-146" />
      <div className="flex-1 mr-3 truncate" data-id="element-147">
        <p className="font-medium truncate" data-id="element-148">{track.title}</p>
        <p className="text-sm text-zinc-400 truncate" data-id="element-149">{track.artist}</p>
      </div>
      <button className="w-10 h-10 rounded-full flex items-center justify-center" onClick={e => {
      e.stopPropagation();
      setIsPlaying(!isPlaying);
    }} data-id="element-150">
        {isPlaying ? <PauseIcon size={24} className="text-white" data-id="element-151" /> : <PlayIcon size={24} className="text-white ml-0.5" data-id="element-152" />}
      </button>
    </div>;
}